import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { PageComponent, PageHeaderComponent } from '@softapps/core';
import {
  BackNavigable,
  BackNavigationService,
  Command,
  CommandStore,
  menuItems,
  Refreshable,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_TITLE,
  subtitle,
  title,
  TitleStore,
} from '@softline/application';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';
import { containsText, Store } from '@softline/core';
import { UiCoreModule } from '@softline/ui-core';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  map,
  Observable,
  startWith,
} from 'rxjs';
import {
  SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
  SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
} from '../../auftrag.shared';
import { EndActivityCommand } from '../../commands/end-activity.command';
import { AuftragCardComponent } from '../../components/auftrag-card/auftrag-card.component';
import { Auftrag } from '../../data/auftrag';
import { Arbeitsbericht } from '../../data/stempel';
import { ScrollPositionService } from '../../services/scroll-position.service';
import { VertreterFilterService } from '../../services/vertreter-filter.service';
import { WerkstattArbeitsberichtStore } from '../../store/werkstatt-arbeitsbericht.store';
import * as WerkstattAuftraegeStore from '../../store/werkstatt-auftraege.store';

@Component({
  selector: 'soft-list-page',
  imports: [
    CommonModule,
    UiCoreModule,
    AuftragCardComponent,
    PageComponent,
    PageHeaderComponent,
  ],
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListComponent
  implements OnInit, OnDestroy, AfterViewInit, Refreshable, BackNavigable
{
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly backNavigationService = inject(BackNavigationService);
  private readonly scrollPositionService = inject(ScrollPositionService);
  public readonly vertreterFilterService = inject(VertreterFilterService);

  readonly searchText$ = new BehaviorSubject<string>('');
  readonly vertreterFilterActive$ = this.vertreterFilterService.vertreterFilterActive$;

  readonly tasks$: Observable<Auftrag[]> = this.store
    .observe(
      SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
      WerkstattAuftraegeStore.getters.all
    )
    .pipe(
      combineLatestWith(
        this.vertreterFilterActive$,
        this.store.observe(
          SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
          AuthenticationContextStore.getters.data
        )
      ),
      map(([auftraege, vertreterFilter, authContext]) =>
        auftraege.filter((auftrag) => {
          if (!vertreterFilter) return true;

          if (!auftrag?.anwenderVertreter) return false;

          return auftrag?.anwenderVertreter?.id === authContext?.['userId'];
        })
      )
    );

  readonly timeReport$: Observable<Arbeitsbericht | undefined> = this.store
    .observe(
      SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
      WerkstattArbeitsberichtStore.getters.active
    )
    .pipe(startWith(undefined));

  readonly filteredAuftraege$ = combineLatest([this.tasks$, this.searchText$]).pipe(
    map(([allTasks, searchString]) => {
      return !searchString || searchString === ''
        ? allTasks
        : allTasks.filter((task) => containsText(task, searchString, true));
    })
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
    WerkstattAuftraegeStore.getters.loading
  );

  readonly loaded$ = this.store.observe(
    SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
    WerkstattAuftraegeStore.getters.loaded
  );

  constructor() {
    title('Werkstätten Aufträge');
    subtitle('#WORKSHOP.TASK_OUTLINE.TITLE');
    menuItems(() => [
      {
        name: 'list',
        title: '#WORKSHOP.TASK_OUTLINE.TITLE',
        icon: 'fa-regular fa-list',
        type: 'route',
        outlet: 'nav',
        routerLink: ['/werkstaettenauftrag'],
      },
      {
        name: 'create-order',
        title: '#WORKSHOP.COMPONENTS.CREATE_TASK.TITLE',
        icon: 'fa-regular fa-pen-to-square',
        type: 'route',
        outlet: 'nav',
        routerLink: ['/werkstaettenauftrag', 'erstellen'],
      },
    ]);
  }

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: ListComponent,
      commands: this.createCommands(),
    });

    await this.refresh(false);
  }

  ngAfterViewInit(): void {
    const auftraege = this.store.get(
      SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
      WerkstattAuftraegeStore.getters.all
    );

    if (auftraege.length > 1) {
      this.scrollPositionService.scrollToLastPosition();
    }
  }

  ngOnDestroy() {
    this.backNavigationService.set(undefined);

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      ListComponent
    );

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, {
      title: '',
    });

    this.scrollPositionService.setScrollPosition();
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  async refresh(clear = true): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
        WerkstattAuftraegeStore.actions.loadMany,
        { clear }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async navigateTo(auftrag: Auftrag): Promise<void> {
    await this.router.navigate(['/werkstaettenauftrag', auftrag.id]);
  }

  protected createCommands(): Command[] {
    const aktiverArbeitsbericht = this.store.get(
      SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
      WerkstattArbeitsberichtStore.getters.active
    );

    return [new EndActivityCommand(this.store, aktiverArbeitsbericht)];
  }
}
